package ui.nearMe

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import linx.linxapp.generated.resources.Res
import linx.linxapp.generated.resources.merchants_near_me
import linx.linxapp.generated.resources.near_me_location_unavailable_message
import linx.linxapp.generated.resources.near_me_location_unavailable_title
import linx.linxapp.generated.resources.near_me_no_stores_message
import linx.linxapp.generated.resources.near_me_no_stores_title
import linx.linxapp.generated.resources.near_me_not_supported_message
import linx.linxapp.generated.resources.near_me_not_supported_title
import linx.linxapp.generated.resources.near_me_open_settings
import linx.linxapp.generated.resources.near_me_permission_always_denied_message
import linx.linxapp.generated.resources.near_me_permission_always_denied_title
import linx.linxapp.generated.resources.near_me_permission_denied_browser_message
import linx.linxapp.generated.resources.near_me_permission_denied_message
import linx.linxapp.generated.resources.near_me_permission_denied_title
import linx.linxapp.generated.resources.near_me_stores_unavailable_message
import linx.linxapp.generated.resources.near_me_stores_unavailable_title
import linx.linxapp.generated.resources.near_me_try_again
import location.GeoLocationPlatformBindings
import model.MerchantSummaryInformation
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource
import ui.Colors
import ui.scrollbar.LazyColumnScrollbar
import ui.search.MerchantNearMeCard

@Composable
fun NearMeScreen() {
    GeoLocationPlatformBindings()

    val nearMeViewModel: NearMeViewModel = nearMeViewModel()

    // Bumping this restarts the location and stores request
    var attempt by remember { mutableIntStateOf(0) }
    val storesFlow = remember(attempt) {
        nearMeViewModel.postStoresNearMe()
    }

    val state by storesFlow.collectAsState(NearMeState.Loading)

    val shouldShowScrollbar = nearMeViewModel.clientConfig.shouldShowScrollBar
    val lazyListState = rememberLazyListState()

    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(12.dp),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        when (val currentState = state) {
            NearMeState.Loading -> {
                CircularProgressIndicator()
            }

            is NearMeState.Error -> {
                NearMeErrorMessage(
                    error = currentState.error,
                    canOpenSettings = nearMeViewModel.canOpenSettings,
                    onOpenSettings = nearMeViewModel::openSettings,
                    onTryAgain = { attempt++ },
                )
            }

            is NearMeState.Success -> {
                val stores = currentState.stores

                if (stores.nearbyStores.isEmpty()) {
                    NearMeMessage(
                        title = Res.string.near_me_no_stores_title,
                        message = Res.string.near_me_no_stores_message,
                        onTryAgain = { attempt++ },
                    )
                } else {
                    LazyColumnScrollbar(
                        listState = lazyListState,
                        padding = 4.dp,
                        alwaysShowScrollBar = shouldShowScrollbar,
                    ) {
                        LazyColumn(
                            state = lazyListState,
                            modifier = Modifier.fillMaxSize()
                        ) {
                            item {
                                Text(
                                    stringResource(Res.string.merchants_near_me),
                                    fontWeight = FontWeight.ExtraBold,
                                    modifier = Modifier
                                        .padding(
                                            start = 16.dp,
                                            top = 16.dp,
                                            bottom = 16.dp
                                        ),
                                    fontSize = 20.sp
                                )
                            }

                            items(stores.nearbyStores) { store ->
                                MerchantNearMeCard(
                                    MerchantSummaryInformation(
                                        name = store.storeName,
                                        phoneNumber = store.phoneNumber.toLongOrNull(),
                                        address = store.address,
                                        loyaltyProgramJoinMessage = store.loyaltyProgramJoinMessage,
                                        dailyOffer = store.dailyOffer,
                                    ),
                                    shouldDeactivateClickEffects = true
                                )

                                HorizontalDivider(
                                    color = Color.White,
                                    thickness = 16.dp,
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun NearMeErrorMessage(
    error: NearMeError,
    canOpenSettings: Boolean,
    onOpenSettings: () -> Unit,
    onTryAgain: () -> Unit,
) {
    when (error) {
        NearMeError.PermissionDenied -> NearMeMessage(
            title = Res.string.near_me_permission_denied_title,
            // Browsers can't be sent to their settings, so point the user to the address bar instead
            message = if (canOpenSettings) {
                Res.string.near_me_permission_denied_message
            } else {
                Res.string.near_me_permission_denied_browser_message
            },
            onTryAgain = onTryAgain,
        )

        NearMeError.PermissionAlwaysDenied -> NearMeMessage(
            title = Res.string.near_me_permission_always_denied_title,
            message = if (canOpenSettings) {
                Res.string.near_me_permission_always_denied_message
            } else {
                Res.string.near_me_permission_denied_browser_message
            },
            onOpenSettings = onOpenSettings.takeIf { canOpenSettings },
            onTryAgain = onTryAgain,
        )

        NearMeError.LocationUnavailable -> NearMeMessage(
            title = Res.string.near_me_location_unavailable_title,
            message = Res.string.near_me_location_unavailable_message,
            onTryAgain = onTryAgain,
        )

        NearMeError.NotSupported -> NearMeMessage(
            title = Res.string.near_me_not_supported_title,
            message = Res.string.near_me_not_supported_message,
        )

        NearMeError.StoresUnavailable -> NearMeMessage(
            title = Res.string.near_me_stores_unavailable_title,
            message = Res.string.near_me_stores_unavailable_message,
            onTryAgain = onTryAgain,
        )
    }
}

@Composable
private fun NearMeMessage(
    title: StringResource,
    message: StringResource,
    onOpenSettings: (() -> Unit)? = null,
    onTryAgain: (() -> Unit)? = null,
) {
    Column(
        modifier = Modifier.padding(horizontal = 16.dp, vertical = 32.dp),
        verticalArrangement = Arrangement.spacedBy(12.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Text(
            stringResource(title),
            fontWeight = FontWeight.Bold,
            fontSize = 18.sp,
            textAlign = TextAlign.Center,
        )
        Text(
            stringResource(message),
            textAlign = TextAlign.Center,
        )
        onOpenSettings?.let {
            Button(
                onClick = it,
                colors = ButtonDefaults.buttonColors(containerColor = Colors.Secondary)
            ) {
                Text(stringResource(Res.string.near_me_open_settings), color = Color.White)
            }
        }
        onTryAgain?.let {
            OutlinedButton(onClick = it) {
                Text(stringResource(Res.string.near_me_try_again), color = Colors.Secondary)
            }
        }
    }
}
